import React from 'react';
import Head from 'next/head';

import { Link, SEO } from '@components';
import CLazyImg from '@components/LazyImage';
import StartTradingFtr from '@appComponents/bb-startTradingFooter';
import { CoinsList } from '@appComponents/bb-coinlistView';

import tradeillus from '@img/tradeillus.svg';

const titleText = 'Trading Mechanism on Bitbns';
const description =
	'Check out the trading mechanism to understand the orders and limit to trade on Bitbns';
const url = 'https://bitbns.com/trade-mechanism/';

const TradingMechanism = () => (
	<>
		<SEO title={titleText} description={description} url={url} />
		<div className="bg-primary text-white py-8">
			<div className="container">
				<h1 className="text-3xl font-bold text-center my-0">
					Trading Mechanism
				</h1>
			</div>
		</div>
		<div className="bg-white">
			<div className="container py-16">
				<div className="mx-auto u-para--readable">
					<div className="border-l-2 border-primary px-4 italic mb-8 ">
						<h3 className="mt-8 font-bold text-primary mb-4">
							Summary
						</h3>
						<p className="u-para--readable text-primary my-4">
							Bitbns uses 'limit trading', which is buying or
							selling Bitcoin at the price you specify, or better.
						</p>
						<p className="u-para--readable text-primary my-4">
							Buying mechanism: If you buy bitcoin at higher than
							market prices, you can 'collect' bitcoin from
							multiple sellers whose rates are lower than yours.
						</p>
						<p className="u-para--readable text-primary my-4">
							Selling mechanism: If you sell bitcoin at lower than
							market prices, you can 'distribute' bitcoins to
							multiple buyers whose rates are higher than yours.
						</p>
						<p className="u-para--readable text-primary my-4">
							If buy and sell prices match, a trade happens, and
							that price is the new market price.
						</p>
					</div>
					<p className="u-para--readable">
						We let you buy and sell cryptocurrencies. The trade
						happens by a particular mechanism, which we shall
						explore below.
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Limit Trading
					</h3>

					<p className="u-para--readable">
						Bitbns currently offers only limit order trading. Limit
						order trading is the buying or selling of a specific
						cryptocurrency at a specified price.
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">Open Orders</h3>
					<ol className="ol--circleNumbered">
						<li className="ol__li mb-2">
							You can only trade when the relevant asset is
							available in your wallet. i.e., when you have
							Bitcoin in your Crypto Wallet, or Rupees in your INR
							wallet
						</li>
						<li className="ol__li mb-2">
							When you place a buy or sell order at a specific
							rate, the order can be seen and tracked under the
							'open orders' section in the dashboard
						</li>
						<li className="ol__li mb-2">
							The order can remain open until it is fully
							executed. You can cancel open orders any time before
							it is fully filled.
						</li>
						<li className="ol__li mb-2">
							Any asset present in an open order will not be
							available for withdrawal until the order is
							cancelled. (Asset denotes Bitcoins for a sell order,
							and Rupees for a buy order.)
						</li>
						<li className="ol__li mb-2">
							Order fulfillment is up to the market. The orders
							will be fulfilled based on the demand or supply of
							Bitcoin.
						</li>
						<li className="ol__li mb-2">
							We don't have any restrictions on the open orders
						</li>
					</ol>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Buy and Sell Orders
					</h3>
					<ol className="ol--circleNumbered ol--circleChecked">
						<li className="ol__li mb-2">
							<b>Buy Orders</b> <br />
							Any user who is willing to buy a specific
							cryptocurrency, the order is placed under buy order
							book. Buy order takes away the liquidity from the
							market. We recommend you buy when the BTC price is
							less, or if you think the price will rise in the
							future.
						</li>
						<li className="ol__li mb-2">
							<b>Sell Orders</b> <br /> Any user who is willing to
							sell a specific cryptocurrency, the order is placed
							under sell order book. Sell order takes away the
							liquidity from the market. We recommend you sell
							when the BTC price is more, or if you think the
							price will drop in the future.
						</li>
					</ol>

					<h3 className="text-lg mt-8 font-bold mb-4">
						How are the Orders Executed?
					</h3>
					<p className="u-para--readable">
						Your order may be fulfilled by a single or multiple
						matching orders, based on the price. Here's how it
						works:
					</p>

					<p className="u-para--readable">
						Let's consider three price points:
					</p>

					<table className="c-table">
						<tbody>
							<tr className="c-table__row">
								<td className="c-table__cell text-jungleGreen">
									B ={' '}
								</td>
								<td className="c-table__cell text-jungleGreen">
									buy price
								</td>
							</tr>

							<tr className="c-table__row">
								<td className="c-table__cell"> T =</td>
								<td className="c-table__cell">
									last trade price
								</td>
							</tr>

							<tr className="c-table__row">
								<td className="c-table__cell text-pastelRed">
									S =
								</td>
								<td className="c-table__cell text-pastelRed">
									sell price
								</td>
							</tr>
						</tbody>
					</table>
					<p className="u-para--readable">
						This leads to three scenarios:
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Case 1: Buy Price &gt; Sell Price
					</h3>

					<p className="u-para--readable">
						If you <span className="text-jungleGreen">buy</span>{' '}
						Bitcoin at a price (
						<span className="text-jungleGreen">B</span>) higher than
						the Sell Price, you can to buy Bitcoins from multiple
						Sellers whose rates (
						<span className="text-pastelRed">S</span>) are lower
						than yours.
					</p>

					<p className="u-para--readable">
						<b>Conclusion:</b> Trade happens at{' '}
						<span className="text-pastelRed">S</span>. Exchange
						price moves to <span className="text-pastelRed">S</span>
						. Here the initiator is the Buyer
					</p>

					<p className="u-para--readable">
						<i>
							<b>Example:</b>
						</i>
						<br />
						Let's assume:
						<br />
						<br />
						Your <span className="text-jungleGreen">Buy</span> price
						= 1 BTC at &#8377;4,92,200
						<br />
						List of <span className="text-pastelRed">
							sell
						</span>{' '}
						orders placed: <br />- 0.3 BTC at &#8377;4,91,000
						<br />- 0.7 BTC at &#8377;4,90,300
						<br />
						<br />
						Then you will be able to buy 1 BTC to all the above sell
						orders at the respective price:
						<br />1 BTC = (0.3&times;4,91,000) +
						(0.7&times;4,90,300) =<b>&#8377;4,90,510</b>
					</p>

					<CLazyImg
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
						src={tradeillus.src}
						className="my-4"
						width="781"
						height="353"
						alt="Trade Illustration"
					/>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Case 2: Sell Price &lt; Buy Price
					</h3>

					<p className="u-para--readable">
						If you sell Bitcoin at a price (
						<span className="text-pastelRed">S</span>) lower than
						the Buy Price, you can to sell Bitcoins to multiple
						buyers whose rates (
						<span className="text-jungleGreen">B</span>) are higher
						than yours.
					</p>

					<p className="u-para--readable">
						<b>Conclusion:</b> Trade happens at{' '}
						<span className="text-jungleGreen">B</span>. Exchange
						price moves to{' '}
						<span className="text-jungleGreen">B</span>. Here the
						initiator is Seller.
					</p>

					<p className="u-para--readable">
						<i>
							<b>Example:</b>
						</i>{' '}
						<br />
						Let's assume <br />
						<br />
						Your <span className="text-pastelRed">sell</span> price
						= 1 BTC at &#8377;4,90,200
						<br />
						List of <span className="text-jungleGreen">
							buy
						</span>{' '}
						orders placed:
						<br />- 0.3 BTC at &#8377;4,91,000
						<br />- 0.7 BTC at &#8377;4,90,300
						<br />
						<br />
						Then you will be able to sell 1 BTC to all the above buy
						orders at the respective price. <br />
						<br />1 BTC = (0.3&times;4,91,000) +
						(0.7&times;4,90,300) = <b>&#8377;4,90,510</b>
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Case 3: Sell Price = Last Trade Price = Buy Price
					</h3>

					<p className="u-para--readable">
						When both buyer and seller are willing to trade at same
						price, order will be executed.
					</p>

					<p className="u-para--readable">
						<b>Conclusion:</b> Trade happens at either of those,
						both being equal.
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">Stop Loss</h3>
					<p className="u-para--readable">
						Minimising the loss is very important when you are
						trading currencies. So Bitbns has added a feature to
						avoid big losses. A user can place a stop-limit during
						the buy order or also after the coin is in the wallet.
					</p>
					<p className="u-para--readable">
						A user can specify the target at which he/she wants to
						exit with a minimum loss when the cryptocurrency is
						falling. We have also added buffer with a target
						stop-limit.
					</p>
					<p className="u-para--readable">
						<b>For example:</b> If a user buys 1 BTC at 10,00,000
						and sets a stop-limit at 9,90,000, then we will set a
						buffer of about 9,90,100. So if at any point of time the
						price falls between 9,90,000 to 9,90,100, the trade will
						be executed.
					</p>
					<p className="u-para--readable">
						<b>Is the stop loss guaranteed?</b>
						<br />
						Stop loss will be executed based on demand and supply,
						i.e., If you hold 10 BTC and if your set stop loss is
						triggered at the price you specified, then during that
						time, provided only 5BTC are in demand, people who have
						set the stop loss first will get the benefit.{' '}
					</p>

					<h3 className="text-lg mt-8 font-bold mb-4">
						Platform Fee:
					</h3>

					<p className="u-para--readable">
						Bitbns charges a fee on each order. Fee will be shown
						before placing an order with a confirmation message.{' '}
					</p>
					<p className="u-para--readable">
						Please check the Bitbns fee for Deposit, Trading, and
						Withdrawal in{' '}
						<Link href="/fees/">
							<b>our fee section</b>
						</Link>
					</p>
				</div>
			</div>
		</div>
		<StartTradingFtr />
		<CoinsList buy price singleLine />
	</>
);

export default TradingMechanism;
